import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from './firebase';

// أنواع البيانات
export interface LinkingCode {
  id: string;
  code: string;
  userId: string;
  userType: 'student' | 'parent' | 'teacher';
  codeType: 'permanent' | 'temporary';
  isActive: boolean;
  usageCount: number;
  maxUsage: number; // -1 للاستخدام اللانهائي
  createdAt: Date;
  updatedAt: Date;
  expiresAt: Date | null;
  createdBy: string;
  metadata: {
    studentGrade?: string;
    parentRelation?: string;
    teacherSubjects?: string[];
    schoolInfo?: any;
  };
}

export interface CodeHistory {
  id: string;
  codeId: string;
  code: string;
  usedBy: string;
  usageType: 'link' | 'verify' | 'register';
  success: boolean;
  errorMessage?: string;
  usedAt: Date;
  metadata?: any;
}

export interface CodeGenerationOptions {
  userType: 'student' | 'parent' | 'teacher';
  codeType?: 'permanent' | 'temporary';
  expirationDays?: number;
  maxUsage?: number;
  metadata?: any;
}

class LinkingCodesService {
  private readonly COLLECTION_NAME = 'linkingCodes';
  private readonly HISTORY_COLLECTION = 'codeHistory';

  // أحرف مسموحة (بدون أحرف مشبوهة)
  private readonly ALLOWED_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

  // بادئات الأكواد
  private readonly CODE_PREFIXES = {
    student: 'S',
    parent: 'P',
    teacher: 'T',
  };

  /**
   * توليد كود عشوائي فريد
   */
  private generateRandomCode(
    userType: 'student' | 'parent' | 'teacher'
  ): string {
    const prefix = this.CODE_PREFIXES[userType];
    let randomPart = '';

    // توليد 8 أحرف عشوائية
    for (let i = 0; i < 8; i++) {
      const randomIndex = Math.floor(Math.random() * this.ALLOWED_CHARS.length);
      randomPart += this.ALLOWED_CHARS[randomIndex];
    }

    return `${prefix}-${randomPart}`;
  }

  /**
   * التحقق من أن الكود فريد
   */
  private async isCodeUnique(code: string): Promise<boolean> {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('code', '==', code)
    );

    const snapshot = await getDocs(q);
    return snapshot.empty;
  }

  /**
   * توليد كود فريد جديد
   */
  private async generateUniqueCode(
    userType: 'student' | 'parent' | 'teacher'
  ): Promise<string> {
    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      const code = this.generateRandomCode(userType);

      if (await this.isCodeUnique(code)) {
        return code;
      }

      attempts++;
    }

    throw new Error('فشل في توليد كود فريد بعد عدة محاولات');
  }

  /**
   * إنشاء كود ربط جديد للمستخدم
   */
  async createLinkingCode(
    userId: string,
    createdBy: string,
    options: CodeGenerationOptions
  ): Promise<LinkingCode> {
    try {
      // التحقق من عدم وجود كود دائم مسبق للمستخدم
      if (options.codeType === 'permanent') {
        const existingCode = await this.getUserPermanentCode(userId);
        if (existingCode) {
          throw new Error('المستخدم لديه كود دائم مسبقاً');
        }
      }

      const code = await this.generateUniqueCode(options.userType);
      const now = new Date();

      // حساب تاريخ انتهاء الصلاحية
      let expiresAt: Date | null = null;
      if (options.codeType === 'temporary' && options.expirationDays) {
        expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + options.expirationDays);
      }

      const linkingCode: Omit<LinkingCode, 'id'> = {
        code,
        userId,
        userType: options.userType,
        codeType: options.codeType || 'permanent',
        isActive: true,
        usageCount: 0,
        maxUsage: options.maxUsage || -1,
        createdAt: now,
        updatedAt: now,
        expiresAt,
        createdBy,
        metadata: options.metadata || {},
      };

      const docRef = doc(collection(db, this.COLLECTION_NAME));
      await setDoc(docRef, {
        ...linkingCode,
        createdAt: Timestamp.fromDate(linkingCode.createdAt),
        updatedAt: Timestamp.fromDate(linkingCode.updatedAt),
        expiresAt: expiresAt ? Timestamp.fromDate(expiresAt) : null,
      });

      return {
        ...linkingCode,
        id: docRef.id,
      };
    } catch (error) {
      console.error('Error creating linking code:', error);
      throw error;
    }
  }

  /**
   * البحث عن كود بالقيمة
   */
  async findCodeByValue(code: string): Promise<LinkingCode | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('code', '==', code.toUpperCase())
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();

      return {
        id: doc.id,
        code: data.code,
        userId: data.userId,
        userType: data.userType,
        codeType: data.codeType,
        isActive: data.isActive,
        usageCount: data.usageCount,
        maxUsage: data.maxUsage,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        expiresAt: data.expiresAt ? data.expiresAt.toDate() : null,
        createdBy: data.createdBy,
        metadata: data.metadata || {},
      };
    } catch (error) {
      console.error('Error finding code:', error);
      return null;
    }
  }

  /**
   * الحصول على الكود الدائم للمستخدم
   */
  async getUserPermanentCode(userId: string): Promise<LinkingCode | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', '==', userId),
        where('codeType', '==', 'permanent'),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();

      return {
        id: doc.id,
        code: data.code,
        userId: data.userId,
        userType: data.userType,
        codeType: data.codeType,
        isActive: data.isActive,
        usageCount: data.usageCount,
        maxUsage: data.maxUsage,
        createdAt: data.createdAt.toDate(),
        updatedAt: data.updatedAt.toDate(),
        expiresAt: data.expiresAt ? data.expiresAt.toDate() : null,
        createdBy: data.createdBy,
        metadata: data.metadata || {},
      };
    } catch (error) {
      console.error('Error getting user permanent code:', error);
      return null;
    }
  }

  /**
   * التحقق من صحة الكود وإمكانية استخدامه
   */
  async validateCode(code: string): Promise<{
    isValid: boolean;
    linkingCode?: LinkingCode;
    error?: string;
  }> {
    try {
      const linkingCode = await this.findCodeByValue(code);

      if (!linkingCode) {
        return {
          isValid: false,
          error: 'الكود غير موجود',
        };
      }

      if (!linkingCode.isActive) {
        return {
          isValid: false,
          linkingCode,
          error: 'الكود غير نشط',
        };
      }

      // التحقق من انتهاء الصلاحية
      if (linkingCode.expiresAt && linkingCode.expiresAt < new Date()) {
        return {
          isValid: false,
          linkingCode,
          error: 'انتهت صلاحية الكود',
        };
      }

      // التحقق من عدد مرات الاستخدام
      if (
        linkingCode.maxUsage > 0 &&
        linkingCode.usageCount >= linkingCode.maxUsage
      ) {
        return {
          isValid: false,
          linkingCode,
          error: 'تم استنفاد عدد مرات استخدام الكود',
        };
      }

      return {
        isValid: true,
        linkingCode,
      };
    } catch (error) {
      console.error('Error validating code:', error);
      return {
        isValid: false,
        error: 'حدث خطأ أثناء التحقق من الكود',
      };
    }
  }

  /**
   * استخدام الكود (زيادة العداد)
   */
  async useCode(
    codeId: string,
    usedBy: string,
    usageType: 'link' | 'verify' | 'register',
    metadata?: any
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const batch = writeBatch(db);

      // تحديث عداد الاستخدام
      const codeRef = doc(db, this.COLLECTION_NAME, codeId);
      batch.update(codeRef, {
        usageCount: (await getDoc(codeRef)).data()?.usageCount + 1 || 1,
        updatedAt: Timestamp.now(),
      });

      // إضافة سجل في التاريخ
      const historyRef = doc(collection(db, this.HISTORY_COLLECTION));
      const historyData: Omit<CodeHistory, 'id'> = {
        codeId,
        code: (await getDoc(codeRef)).data()?.code || '',
        usedBy,
        usageType,
        success: true,
        usedAt: new Date(),
        metadata,
      };

      batch.set(historyRef, {
        ...historyData,
        usedAt: Timestamp.fromDate(historyData.usedAt),
      });

      await batch.commit();

      return { success: true };
    } catch (error) {
      console.error('Error using code:', error);

      // تسجيل الفشل في التاريخ
      try {
        const historyRef = doc(collection(db, this.HISTORY_COLLECTION));
        await setDoc(historyRef, {
          codeId,
          code: '',
          usedBy,
          usageType,
          success: false,
          errorMessage:
            error instanceof Error ? error.message : 'خطأ غير معروف',
          usedAt: Timestamp.now(),
          metadata,
        });
      } catch (historyError) {
        console.error('Error logging failed usage:', historyError);
      }

      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'حدث خطأ أثناء استخدام الكود',
      };
    }
  }

  /**
   * إلغاء تفعيل الكود
   */
  async deactivateCode(codeId: string): Promise<boolean> {
    try {
      const codeRef = doc(db, this.COLLECTION_NAME, codeId);
      await updateDoc(codeRef, {
        isActive: false,
        updatedAt: Timestamp.now(),
      });
      return true;
    } catch (error) {
      console.error('Error deactivating code:', error);
      return false;
    }
  }

  /**
   * الحصول على جميع الأكواد للمستخدم
   */
  async getUserCodes(userId: string): Promise<LinkingCode[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          code: data.code,
          userId: data.userId,
          userType: data.userType,
          codeType: data.codeType,
          isActive: data.isActive,
          usageCount: data.usageCount,
          maxUsage: data.maxUsage,
          createdAt: data.createdAt.toDate(),
          updatedAt: data.updatedAt.toDate(),
          expiresAt: data.expiresAt ? data.expiresAt.toDate() : null,
          createdBy: data.createdBy,
          metadata: data.metadata || {},
        };
      });
    } catch (error) {
      console.error('Error getting user codes:', error);
      return [];
    }
  }

  /**
   * تنظيف الأكواد المنتهية الصلاحية
   */
  async cleanupExpiredCodes(): Promise<number> {
    try {
      const now = new Date();
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('expiresAt', '<=', Timestamp.fromDate(now)),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      const batch = writeBatch(db);

      snapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          isActive: false,
          updatedAt: Timestamp.now(),
        });
      });

      await batch.commit();
      return snapshot.size;
    } catch (error) {
      console.error('Error cleaning up expired codes:', error);
      return 0;
    }
  }

  /**
   * الحصول على تاريخ استخدام الكود
   */
  async getCodeHistory(codeId: string): Promise<CodeHistory[]> {
    try {
      const q = query(
        collection(db, this.HISTORY_COLLECTION),
        where('codeId', '==', codeId),
        orderBy('usedAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          codeId: data.codeId,
          code: data.code,
          usedBy: data.usedBy,
          usageType: data.usageType,
          success: data.success,
          errorMessage: data.errorMessage,
          usedAt: data.usedAt.toDate(),
          metadata: data.metadata,
        };
      });
    } catch (error) {
      console.error('Error getting code history:', error);
      return [];
    }
  }

  /**
   * الحصول على جميع الأكواد
   */
  async getAllCodes(): Promise<LinkingCode[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          code: data.code,
          userId: data.userId,
          userType: data.userType || 'student',
          codeType: data.codeType || 'permanent',
          isActive: data.isActive ?? true,
          usageCount: data.usageCount || 0,
          maxUsage: data.maxUsage || -1,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate() || null,
          createdBy: data.createdBy || '',
          metadata: data.metadata || {},
          linkedUserIds: data.linkedUserIds || [],
        } as LinkingCode & { linkedUserIds?: string[] };
      });
    } catch (error) {
      console.error('Error getting all codes:', error);
      return [];
    }
  }

  /**
   * تحديث العلاقات والروابط لكود محدد
   */
  async updateCodeRelations(
    codeId: string,
    linkedUserIds: string[]
  ): Promise<boolean> {
    try {
      const codeRef = doc(db, this.COLLECTION_NAME, codeId);
      await updateDoc(codeRef, {
        linkedUserIds: linkedUserIds,
        updatedAt: Timestamp.now(),
      });

      console.log(`Updated relations for code ${codeId}:`, linkedUserIds);
      return true;
    } catch (error) {
      console.error('Error updating code relations:', error);
      throw error;
    }
  }
}

export const linkingCodesService = new LinkingCodesService();
export default linkingCodesService;
